/**
 * What the hardware service download page needs to know, with none of the rendering.
 *
 * Nothing here decides entitlement. The backend already refuses to return a release the
 * caller may not have, and re-deriving that rule in the browser would create a second
 * definition of it that could drift. What the page does need is the *presentation* of what it
 * was given: which build matches this workstation, how big it is, whether it is signed, and
 * whether the channel it came from is one an operator should be warned about.
 */

export type ReleasePlatform = 'windows' | 'macos';

export type ReleaseArchitecture = 'x64' | 'arm64';

export type ReleaseChannel = 'internal' | 'qa' | 'stable';

/** A release as the API returns it. */
export interface HardwareServiceRelease {
    version: string;
    platform: ReleasePlatform;
    architecture: ReleaseArchitecture;
    channel: ReleaseChannel;
    filename: string;
    sizeBytes: number;
    sha256: string;
    signed: boolean;
    releaseNotes: string | null;
    publishedAt: string;
    yankedAt: string | null;
}

/** One installable target, as the platform and architecture selectors offer it. */
export interface ReleaseTarget {
    platform: ReleasePlatform;
    architecture: ReleaseArchitecture;
}

/**
 * What this browser is probably running on.
 *
 * A suggestion, never a decision. The page pre-selects it and leaves both selectors editable,
 * because an operator downloading an installer for the machine next to them is an ordinary
 * thing to do and a user agent is a poor witness even about its own host.
 */
export function detectPlatform(userAgent: string): ReleasePlatform {
    return /mac os|macintosh|darwin/i.test(userAgent) ? 'macos' : 'windows';
}

/**
 * The likely architecture.
 *
 * Defaults to `x64` because that is the only architecture the packaging currently produces,
 * so a wrong guess costs an operator one dropdown change rather than a failed install.
 */
export function detectArchitecture(userAgent: string): ReleaseArchitecture {
    return /arm64|aarch64/i.test(userAgent) ? 'arm64' : 'x64';
}

/** The target this browser suggests. */
export function detectReleaseTarget(userAgent: string): ReleaseTarget {
    return {
        platform: detectPlatform(userAgent),
        architecture: detectArchitecture(userAgent),
    };
}

/**
 * A size an operator can compare against what their browser reports having downloaded.
 *
 * Binary units, one decimal place. An installer is a file on disk, and a file manager will say
 * "90.0 MB" for the same bytes; showing a decimal megabyte here would look like a mismatch.
 */
export function formatArtifactSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) {
        return '—';
    }

    if (bytes < 1024) {
        return `${bytes} B`;
    }

    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value.toFixed(1)} ${units[unitIndex]}`;
}

/**
 * True for a channel that must be labelled unmistakably wherever it is offered.
 *
 * `stable` is the only channel an ordinary operator ever sees. Anything else is visible only to
 * an administrator and is, by construction, a build that has not been through whatever the
 * stable channel means — so it is never offered without saying so.
 */
export function isRestrictedChannel(channel: ReleaseChannel): boolean {
    return channel !== 'stable';
}

/** Numeric version ordering. `1.10.0` is newer than `1.9.0`, which string ordering denies. */
export function compareReleaseVersions(left: string, right: string): number {
    const leftParts = left.split('.').map(Number);
    const rightParts = right.split('.').map(Number);

    for (let index = 0; index < 3; index += 1) {
        const difference = (leftParts[index] ?? 0) - (rightParts[index] ?? 0);

        if (difference !== 0) {
            return difference;
        }
    }

    return 0;
}

/** Newest first, then by platform and architecture so a version's rows keep a stable order. */
export function sortReleasesForDisplay(
    releases: readonly HardwareServiceRelease[],
): HardwareServiceRelease[] {
    return [...releases].sort(compareForDisplay);
}

/** Module scope so the sort callback does not nest inside another function (S2004). */
function compareForDisplay(left: HardwareServiceRelease, right: HardwareServiceRelease): number {
    const byVersion = compareReleaseVersions(right.version, left.version);

    if (byVersion !== 0) {
        return byVersion;
    }

    const byPlatform = left.platform.localeCompare(right.platform);

    return byPlatform !== 0 ? byPlatform : left.architecture.localeCompare(right.architecture);
}

/** The build matching a chosen target, or null when that combination was never published. */
export function findReleaseForTarget(
    releases: readonly HardwareServiceRelease[],
    target: ReleaseTarget,
): HardwareServiceRelease | null {
    const matches = releases.filter(
        release =>
            release.platform === target.platform && release.architecture === target.architecture,
    );

    return sortReleasesForDisplay(matches)[0] ?? null;
}

/**
 * Every target that actually has a build, newest-version order.
 *
 * Offering a selector full of combinations that were never published invites an operator to
 * pick one and be told there is nothing there. The selectors are built from what exists.
 */
export function listAvailableTargets(releases: readonly HardwareServiceRelease[]): ReleaseTarget[] {
    const seen = new Set<string>();
    const targets: ReleaseTarget[] = [];

    for (const release of sortReleasesForDisplay(releases)) {
        const key = `${release.platform}/${release.architecture}`;

        if (!seen.has(key)) {
            seen.add(key);
            targets.push({ platform: release.platform, architecture: release.architecture });
        }
    }

    return targets;
}

/** The authorized download path for a release. Query parameters, never path segments. */
export function buildReleaseDownloadPath(release: HardwareServiceRelease): string {
    const query = new URLSearchParams({
        platform: release.platform,
        architecture: release.architecture,
    });

    return `/hardware-service/releases/${release.version}/download?${query.toString()}`;
}
